// The parent-notification templates (build plan §10.1 #4; D2 §F; §13 #6).
// Pure: Config's editor (keys.ts), the queue and the renderer read these.
//
//   * One MedConfig key per template, its wording editable on the Config
//     screen. The D2 §F wording ships VERBATIM where §F has it (the minor
//     visit / OTC email and the follow-up email after the call), the program
//     name injected through [Program name]. The phone script, the dismissal
//     email and the two AdultSelf variants are NEW wording in §F's style
//     (owner, 2026-10-19): §F has none.
//   * Each carries a status: "Draft" (shown everywhere as "Draft — RN review
//     pending") until an RN sets it "Approved" on Config. A draft made from a
//     template still in Draft can be released (this is a beta); the badge
//     is information. Changing the wording of an approved template returns
//     it to Draft.
//   * [Brackets] are filled from the visit when the message is shown or
//     sent. Whatever no record can fill (the current status, next steps…)
//     stays in brackets, and a message with a bracket left is not sent.


export const TEMPLATE_KEYS = [
  "NotifyTemplateMinorEmail",
  "NotifyTemplatePhoneScript",
  "NotifyTemplateFollowUpEmail",
  "NotifyTemplateDismissalEmail",
  "NotifyTemplateAdultSelfEmail",
  "NotifyTemplateAdultSelfFollowUpEmail",
] as const;
export type TemplateKey = (typeof TEMPLATE_KEYS)[number];

export const TEMPLATE_STATUSES = ["Draft", "Approved"] as const;
export const TEMPLATE_DRAFT_BADGE = "Draft — RN review pending";

export interface TemplateValue {
  subject: string;
  body: string;
  status: "Draft" | "Approved";
  /** Who approved it (an RN's email) and when (YYYY-MM-DD); set by the server. */
  approvedBy?: string;
  approvedOn?: string;
  note?: string;
}

export interface TemplateDef {
  key: TemplateKey;
  label: string;
  /** Where the wording comes from: D2 §F verbatim, or new wording for RN review. */
  origin: "D2 §F" | "New wording";
  help: string;
  /** Which occurrence of a repeated [bracket] means what (the n-th [time] is the n-th value). */
  times: ("reached" | "visit" | "referral" | "release")[];
  staffNames: ("reachedBy" | "accompaniedBy" | "releaser")[];
  defaults: { subject: string; body: string };
}

/**
 * The signature: whoever releases the email, by display name and the
 * signature title on their user record (MedUsers.SignatureTitle: "Russell
 * Myers, Director", "Jane Sample, RN"); a blank title signs the name alone,
 * no comma. Not the role, and never a literal "RN" (owner, 2026-10-19): D2
 * §F's "[RN name], RN" became this placeholder in every template that signs.
 */
export const RELEASER_PLACEHOLDER = "[Releaser name, title]";
const SIGN = [RELEASER_PLACEHOLDER, "[Program name] Medical Staff"].join("\n");

/** "Name, Signature title" for the signature, or the name alone when the title is blank. */
export function releaserLine(u: { name: string; signatureTitle?: string }): string {
  const title = (u.signatureTitle ?? "").trim();
  return title ? `${u.name}, ${title}` : u.name;
}

/** A template saved before the placeholder changed still signs with the releaser's own title. */
const legacySignature = (text: string) => text.replace(/\[RN name\], RN\b/g, RELEASER_PLACEHOLDER).replace(/\[RN name\]/g, RELEASER_PLACEHOLDER);

export const TEMPLATE_DEFS: readonly TemplateDef[] = [
  {
    key: "NotifyTemplateMinorEmail",
    label: "Parent email: minor visit or OTC given",
    origin: "D2 §F",
    help: "Email tier, sent within 24 hours of a minor visit or an OTC item given for a complaint.",
    times: ["visit"],
    staffNames: [],
    defaults: {
      subject: "[Program name] — medical station visit for [Student first name]",
      body: [
        "Dear [Parent/Guardian name],",
        "",
        "This is a courtesy notice that [Student first name] visited the [Program name] medical station on [date] at [time].",
        "",
        "Reason for visit: [complaint, plain language]",
        'Care provided: [treatment, e.g. "Acetaminophen 500 mg given at 2:15 PM, as authorized on your application"]',
        'Status: [disposition, e.g. "Returned to program activities" / "Resting in city for the afternoon; staff will check on them"]',
        "",
        "No further action is needed on your part. If you have questions, or if there is anything about [Student first name]'s health you would like our medical staff to know, please call the medical station at [phone] or reply to this email.",
        "",
        SIGN,
      ].join("\n"),
    },
  },
  {
    key: "NotifyTemplatePhoneScript",
    label: "Parent call: injury, referral or transport (phone script)",
    origin: "New wording",
    help: "Phone tier: the prompt the caller reads before and during the call. Not sent to anyone; the follow-up email goes after the parent is reached.",
    times: ["visit"],
    staffNames: [],
    defaults: {
      subject: "Call about [Student first name] ([Program name] medical station)",
      body: [
        "Script prompt for the call (say it in your own words):",
        "- Identify yourself and the program: \"This is [staff name] from the [Program name] medical staff.\"",
        "- Say first how [Student first name] is right now.",
        "- What happened: [Student first name] was seen at the medical station on [date] at [time] for [complaint].",
        "- What was done, and where [Student first name] is now: [treatment]; [disposition].",
        "- If [Student first name] was referred or taken for outside care: where, when, and who is with them.",
        "- What happens next, and when you will call again.",
        "- Answer questions; give the medical station number, [phone].",
        "- Log the call: the time, the number dialled, who you spoke to, and a summary of what was said. An email follows the call.",
      ].join("\n"),
    },
  },
  {
    key: "NotifyTemplateFollowUpEmail",
    label: "Parent email: follow-up after the call (referral or outside care)",
    origin: "D2 §F",
    help: "Drafted when the parent is reached on a phone-tier visit.",
    times: ["reached", "visit", "referral"],
    staffNames: ["reachedBy", "accompaniedBy", "reachedBy"],
    defaults: {
      subject: "[Program name] — follow-up to our call about [Student first name]",
      body: [
        "Dear [Parent/Guardian name],",
        "",
        "As discussed by phone at [time] with [staff name], [Student first name] was seen at the medical station on [date] at [time] for [complaint] and was referred to [urgent care / the emergency department at … / remote provider consult] at [time], accompanied by [staff name].",
        "",
        "[Current status / expected return / next steps.] Our medical staff will contact you again when [Student first name] returns or if anything changes.",
        "",
        "Please contact [staff name] at [phone] at any time.",
        "",
        SIGN,
      ].join("\n"),
    },
  },
  {
    key: "NotifyTemplateDismissalEmail",
    label: "Parent email: medical dismissal (after the call)",
    origin: "New wording",
    help: "Drafted when the parent is reached about a medical dismissal.",
    times: ["reached", "visit", "release"],
    staffNames: ["reachedBy", "reachedBy"],
    defaults: {
      subject: "[Program name] — [Student first name] is leaving the program",
      body: [
        "Dear [Parent/Guardian name],",
        "",
        "As discussed by phone at [time] with [staff name], [Student first name] was seen at the medical station on [date] at [time] for [complaint] and is leaving the program early for medical reasons.",
        "",
        "[Student first name] was released to [released to] at [time]. [Student first name]'s medications will be returned [how and when].",
        "",
        "[Anything the family should watch for or do next.] Please contact [staff name] at [phone] with any questions.",
        "",
        SIGN,
      ].join("\n"),
    },
  },
  {
    key: "NotifyTemplateAdultSelfEmail",
    label: "Attendee email (18 or older, no consent): minor visit or OTC given",
    origin: "New wording",
    help: "For an attendee 18 or older who has not agreed to parent notification (D2 #8): the notice goes to them.",
    times: ["visit"],
    staffNames: [],
    defaults: {
      subject: "[Program name] — your medical station visit",
      body: [
        "Dear [Student first name],",
        "",
        "This is a record of your visit to the [Program name] medical station on [date] at [time].",
        "",
        "Reason for visit: [complaint, plain language]",
        "Care provided: [treatment]",
        "Status: [disposition]",
        "",
        "Because you are 18 or older and have not asked us to tell a parent or guardian, this notice comes to you. You can change that at the medical station at any time. If you have questions, call the medical station at [phone] or reply to this email.",
        "",
        SIGN,
      ].join("\n"),
    },
  },
  {
    key: "NotifyTemplateAdultSelfFollowUpEmail",
    label: "Attendee email (18 or older, no consent): follow-up after a referral",
    origin: "New wording",
    help: "For an attendee 18 or older who has not agreed to parent notification, after a phone-tier visit.",
    times: ["visit", "referral"],
    staffNames: ["releaser"],
    defaults: {
      subject: "[Program name] — follow-up to your medical station visit",
      body: [
        "Dear [Student first name],",
        "",
        "Following up on your visit to the medical station on [date] at [time] for [complaint]: you were referred to [urgent care / the emergency department at … / remote provider consult] at [time].",
        "",
        "[Current status / next steps.] Because you are 18 or older and have not asked us to tell a parent or guardian, this notice comes to you; you can change that at the medical station at any time.",
        "",
        "Please contact [staff name] at [phone] at any time.",
        "",
        SIGN,
      ].join("\n"),
    },
  },
];

export function templateDef(key: string): TemplateDef | null {
  return TEMPLATE_DEFS.find((d) => d.key === key) ?? null;
}

export const isTemplateKey = (key: string): key is TemplateKey => (TEMPLATE_KEYS as readonly string[]).includes(key);

/** The stored value, or the shipped default (status Draft) when the row is blank. */
export function templateValue(key: TemplateKey, stored: unknown): TemplateValue {
  const d = templateDef(key)!;
  const v = (stored && typeof stored === "object" ? stored : {}) as Partial<TemplateValue>;
  return {
    subject: typeof v.subject === "string" && v.subject.trim() ? v.subject : d.defaults.subject,
    body: typeof v.body === "string" && v.body.trim() ? v.body : d.defaults.body,
    status: v.status === "Approved" ? "Approved" : "Draft",
    approvedBy: v.approvedBy,
    approvedOn: v.approvedOn,
  };
}

/** Which template a notification row is drafted from (the name is kept: derived from the row, never stored apart). */
export function templateKeyFor(o: { tier: string; method: string; serious: boolean; dismissed: boolean }): TemplateKey {
  if (o.method === "Phone") return "NotifyTemplatePhoneScript";
  if (o.tier === "AdultSelf") return o.serious ? "NotifyTemplateAdultSelfFollowUpEmail" : "NotifyTemplateAdultSelfEmail";
  if (o.tier === "Phone") return o.dismissed ? "NotifyTemplateDismissalEmail" : "NotifyTemplateFollowUpEmail";
  return "NotifyTemplateMinorEmail";
}

/** What fills the brackets. A repeated bracket takes its values in order (TemplateDef.times / staffNames). */
export interface TemplateVars {
  programName: string;
  firstName: string;
  parentName: string;
  date: string;
  times: Partial<Record<TemplateDef["times"][number], string>>;
  staffNames: Partial<Record<TemplateDef["staffNames"][number], string>>;
  complaint: string;
  treatment: string;
  disposition: string;
  referredTo: string;
  releasedTo: string;
  phone: string;
  /** The releaser's signature, "Name, Signature title" or the name alone (releaserLine): filled at release only. */
  releaser: string;
}

/**
 * Fills every bracket a record can fill; leaves the rest in brackets. A
 * bracket is known by what it starts with ("[treatment, e.g. …]" is the
 * treatment).
 */
export function renderTemplate(def: TemplateDef, text: string, v: TemplateVars): string {
  let timeN = 0;
  let staffN = 0;
  return legacySignature(text).replace(/\[([^\]]+)\]/g, (whole, inner: string) => {
    const k = inner.trim().toLowerCase();
    const or = (x: string | undefined) => (x && x.trim() ? x : whole);
    if (k === "program name") return or(v.programName);
    if (k === "student first name") return or(v.firstName);
    if (k === "parent/guardian name") return or(v.parentName);
    if (k === "date") return or(v.date);
    if (k === "time") {
      const which = def.times[Math.min(timeN++, def.times.length - 1)];
      return or(which ? v.times[which] : "");
    }
    if (k === "staff name") {
      const which = def.staffNames[Math.min(staffN++, Math.max(def.staffNames.length - 1, 0))];
      return or(which ? v.staffNames[which] : "");
    }
    if (k.startsWith("complaint")) return or(v.complaint);
    if (k.startsWith("treatment")) return or(v.treatment);
    if (k.startsWith("disposition")) return or(v.disposition);
    if (k.startsWith("urgent care /")) return or(v.referredTo);
    if (k === "released to") return or(v.releasedTo);
    if (k === "phone") return or(v.phone);
    if (k === "releaser name, title") return or(v.releaser);
    return whole;
  });
}

/** The brackets still in a message (nothing is sent while there are any). */
export function bracketsLeft(text: string): string[] {
  return [...new Set([...text.matchAll(/\[[^\]]+\]/g)].map((m) => m[0]))];
}
